import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { BannerVariant } from '@components/ui/Banner/Banner';

// ============================================
// TYPES
// ============================================

export interface Notification {
  id: string;
  variant: BannerVariant;
  title: string;
  description?: string;
}

interface NotificationState {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
}

// ============================================
// HELPERS
// ============================================

let counter = 0;

function generateId(): string {
  return `notif_${Date.now()}_${++counter}`;
}

// ============================================
// AUTO-DISMISS DURATION (ms)
// ============================================

const AUTO_DISMISS_MS = 5000;

// ============================================
// STORE
// ============================================

export const useNotificationStore = create<NotificationState>()(
  devtools(
    (set) => ({
      notifications: [],

      addNotification: (notification) => {
        const id = generateId();
        set(
          (state) => ({
            notifications: [...state.notifications, { ...notification, id }],
          }),
          undefined,
          'addNotification',
        );

        // Auto-dismiss after timeout
        setTimeout(() => {
          set(
            (state) => ({
              notifications: state.notifications.filter((n) => n.id !== id),
            }),
            undefined,
            'removeNotification/auto',
          );
        }, AUTO_DISMISS_MS);
      },

      removeNotification: (id) =>
        set(
          (state) => ({
            notifications: state.notifications.filter((n) => n.id !== id),
          }),
          undefined,
          'removeNotification',
        ),
    }),
    { name: 'notification-store' },
  ),
);
